// src/components/community/VillageAdminConsole.tsx
// DROP 38 — 22 Villages Full Admin Console
// Master dashboard for all 26 communities (22 villages + 4 special)

import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, Settings, CheckCircle, XCircle, AlertTriangle,
  Eye, Edit3, Download, Upload, Search, Filter,
  Users, Globe, BarChart3, Zap, Crown, MapPin,
  Plus, Trash2, Save, X
} from 'lucide-react';

// ── Design Tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:'#070D0A', surface:'#0E1812', card:'#121E16', border:'#1C2E22',
  text:'#EDE8DC', muted:'rgba(237,232,220,0.5)', green:'#0F5132',
  gold:'#C9A84C', goldLt:'#E2C97E', red:'#ef4444',
  orange:'#f97316', blue:'#60a5fa',
};

// ── Canonical 26 Communities ──────────────────────────────────────────────────
const ALL_COMMUNITIES = [
  { id:'v01', name:'Jattu-Uzairue',       icon:'👑', type:'village', isCapital:true },
  { id:'v02', name:'Afashio-Uzairue',     icon:'🛡️', type:'village' },
  { id:'v03', name:'Afowa-Uzairue',       icon:'🌳', type:'village' },
  { id:'v04', name:'Apana-Uzairue',       icon:'🌊', type:'village' },
  { id:'v05', name:'Ayaoghena-Uzairue',   icon:'🎭', type:'village' },
  { id:'v06', name:'Ayogwiri-Uzairue',    icon:'⚔️', type:'village' },
  { id:'v07', name:'Ayua-Uzairue',        icon:'🌿', type:'village' },
  { id:'v08', name:'Elele-Uzairue',       icon:'🌾', type:'village' },
  { id:'v09', name:'Idatto-Uzairue',      icon:'🏺', type:'village' },
  { id:'v10', name:'Ikabigbo-Uzairue',    icon:'🥊', type:'village' },
  { id:'v11', name:'Ikholo-Uzairue',      icon:'⚖️', type:'village' },
  { id:'v12', name:'Imeke-Uzairue',       icon:'🧵', type:'village' },
  { id:'v13', name:'Imhonikhe-Uzairue',   icon:'🏛️', type:'village' },
  { id:'v14', name:'Irekpai-Uzairue',     icon:'⚒️', type:'village' },
  { id:'v15', name:'Iyamho-Uzairue',      icon:'📚', type:'village' },
  { id:'v16', name:'Iyora-Uzairue',       icon:'🎪', type:'village' },
  { id:'v17', name:'Iyuku-Uzairue',       icon:'🏪', type:'village' },
  { id:'v18', name:'Ogbido-Uzairue',      icon:'🌿', type:'village' },
  { id:'v19', name:'Ozor-Uzairue',        icon:'🌲', type:'village' },
  { id:'v20', name:'Ugbenor-Uzairue',     icon:'🌴', type:'village' },
  { id:'v21', name:'Uluoke-Uzairue',      icon:'⛰️', type:'village' },
  { id:'v22', name:'Yelwa-Uzairue',       icon:'💰', type:'village' },
  { id:'sp01', name:'Igala Community',    icon:'🤝', type:'special' },
  { id:'sp02', name:'Igbo Community',     icon:'🌟', type:'special' },
  { id:'sp03', name:'Yoruba Community',   icon:'🎺', type:'special' },
  { id:'sp04', name:'Diaspora Community', icon:'✈️', type:'special' },
];

interface PortalStatus {
  villageId: string;
  villageName: string;
  isInitialized: boolean;
  published: boolean;
  hasHero: boolean;
  hasChief: boolean;
  hasHistory: boolean;
  hasGallery: boolean;
  hasContact: boolean;
  teamCount: number;
  postCount: number;
  resourceCount: number;
  completionPct: number;
  updatedAt?: string;
}

interface Props {
  currentUser?: any;
  onSelectVillage?: (village: { id: string; name: string }) => void;
}

const authHeader = () => {
  const t = localStorage.getItem('erue_auth_token') || sessionStorage.getItem('erue_auth_token') || '';
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` };
};

export default function VillageAdminConsole({ currentUser, onSelectVillage }: Props) {
  type Tab = 'dashboard'|'portals'|'contributions'|'export';
  const [tab, setTab]             = useState<Tab>('dashboard');
  const [statuses, setStatuses]   = useState<PortalStatus[]>([]);
  const [loading, setLoading]     = useState(true);
  const [initing, setIniting]     = useState(false);
  const [msg, setMsg]             = useState<string|null>(null);
  const [search, setSearch]       = useState('');
  const [filterType, setFilterType] = useState<'all'|'village'|'special'>('all');
  const [filterStatus, setFilterStatus] = useState<'all'|'published'|'draft'|'empty'>('all');
  const [contributions, setContributions] = useState<any[]>([]);
  const [contribLoading, setContribLoading] = useState(false);

  const role    = (currentUser?.systemRole || currentUser?.role || '').toUpperCase();
  const isAdmin = ['PLATFORM_ADMIN','FESTIVAL_COORDINATOR','COMMUNITY_MANAGER'].includes(role);

  const showMsg = (m: string) => { setMsg(m); setTimeout(() => setMsg(null), 4000); };

  // ── Fetch portal statuses ─────────────────────────────────────────────────
  const fetchStatuses = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/admin/community-portals/status', { headers: authHeader() });
      const d = await r.json();
      if (d.success) {
        // Merge server data with canonical community list
        const serverMap: Record<string, any> = {};
        (d.portals || d.statuses || []).forEach((p: any) => { serverMap[p.villageId] = p; });

        const merged: PortalStatus[] = ALL_COMMUNITIES.map(c => {
          const s = serverMap[c.id];
          if (!s) return {
            villageId: c.id, villageName: c.name,
            isInitialized: false, published: false,
            hasHero: false, hasChief: false, hasHistory: false,
            hasGallery: false, hasContact: false,
            teamCount: 0, postCount: 0, resourceCount: 0, completionPct: 0,
          };
          const checks = [s.hasHero||s.heroImageUrl, s.hasChief||s.chiefName,
                          s.hasHistory||s.history, s.hasGallery||(s.galleryCount>0),
                          s.hasContact||s.contactEmail];
          const done = checks.filter(Boolean).length;
          return {
            villageId: c.id, villageName: c.name,
            isInitialized: true,
            published: s.published || s.isPublished || false,
            hasHero: !!(s.hasHero || s.heroImageUrl),
            hasChief: !!(s.hasChief || s.chiefName),
            hasHistory: !!(s.hasHistory || (s.history && s.history.length > 50)),
            hasGallery: !!(s.hasGallery || s.galleryCount > 0),
            hasContact: !!(s.hasContact || s.contactEmail || s.contactPhone),
            teamCount: s.teamCount || 0,
            postCount: s.postCount || 0,
            resourceCount: s.resourceCount || 0,
            completionPct: Math.round((done / checks.length) * 100),
            updatedAt: s.updatedAt,
          };
        });
        setStatuses(merged);
      } else {
        // If route fails, show all as uninitialized
        setStatuses(ALL_COMMUNITIES.map(c => ({
          villageId: c.id, villageName: c.name,
          isInitialized: false, published: false,
          hasHero: false, hasChief: false, hasHistory: false,
          hasGallery: false, hasContact: false,
          teamCount: 0, postCount: 0, resourceCount: 0, completionPct: 0,
        })));
      }
    } catch {
      setStatuses(ALL_COMMUNITIES.map(c => ({
        villageId: c.id, villageName: c.name, isInitialized: false, published: false,
        hasHero: false, hasChief: false, hasHistory: false,
        hasGallery: false, hasContact: false,
        teamCount: 0, postCount: 0, resourceCount: 0, completionPct: 0,
      })));
    } finally { setLoading(false); }
  }, []);

  const fetchContributions = useCallback(async () => {
    setContribLoading(true);
    try {
      const r = await fetch('/api/admin/village-contributions', { headers: authHeader() });
      const d = await r.json();
      if (d.success) setContributions(d.contributions || []);
    } catch { /* silent */ }
    finally { setContribLoading(false); }
  }, []);

  useEffect(() => { fetchStatuses(); }, [fetchStatuses]);
  useEffect(() => { if (tab === 'contributions') fetchContributions(); }, [tab, fetchContributions]);

  // ── Bulk initialize all portals ───────────────────────────────────────────
  const handleInitAll = async () => {
    if (!confirm('Initialize all 26 community portals? This will create empty portals for any not yet initialized. Existing portals will NOT be overwritten.')) return;
    setIniting(true);
    try {
      const r = await fetch('/api/admin/community-portals/init-all', { headers: authHeader() });
      const d = await r.json();
      if (d.success) {
        showMsg(`✅ Initialized ${d.created || 0} new portals. ${d.existing || 0} already existed.`);
        fetchStatuses();
      } else showMsg('❌ ' + (d.error || 'Init failed'));
    } catch { showMsg('❌ Network error'); }
    finally { setIniting(false); }
  };

  // ── Export all village data as CSV ────────────────────────────────────────
  const handleExport = () => {
    const headers = ['ID','Village Name','Type','Initialized','Published','Chief','History','Gallery','Contact','Team','Posts','Resources','Completion %','Last Updated'];
    const rows = statuses.map(s => {
      const c = ALL_COMMUNITIES.find(x => x.id === s.villageId);
      return [
        s.villageId, s.villageName, c?.type || 'village',
        s.isInitialized ? 'Yes' : 'No',
        s.published ? 'Yes' : 'No',
        s.hasChief ? 'Yes' : 'No',
        s.hasHistory ? 'Yes' : 'No',
        s.hasGallery ? 'Yes' : 'No',
        s.hasContact ? 'Yes' : 'No',
        String(s.teamCount),
        String(s.postCount),
        String(s.resourceCount),
        s.completionPct + '%',
        s.updatedAt ? new Date(s.updatedAt).toLocaleDateString('en-GB') : '—',
      ].map(v => `"${String(v).replace(/"/g,'""')}"`).join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `uzairue_villages_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showMsg('✅ CSV exported successfully');
  };

  // ── Computed stats ────────────────────────────────────────────────────────
  const initialized  = statuses.filter(s => s.isInitialized).length;
  const published    = statuses.filter(s => s.published).length;
  const complete     = statuses.filter(s => s.completionPct >= 80).length;
  const avgCompletion = statuses.length > 0
    ? Math.round(statuses.reduce((sum, s) => sum + s.completionPct, 0) / statuses.length)
    : 0;

  // ── Filter ────────────────────────────────────────────────────────────────
  const filtered = statuses.filter(s => {
    const c = ALL_COMMUNITIES.find(x => x.id === s.villageId);
    if (filterType !== 'all' && c?.type !== filterType) return false;
    if (filterStatus === 'published' && !s.published) return false;
    if (filterStatus === 'draft' && (!s.isInitialized || s.published)) return false;
    if (filterStatus === 'empty' && s.isInitialized) return false;
    if (search && !s.villageName.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // ── Guard ─────────────────────────────────────────────────────────────────
  if (!currentUser || !isAdmin) return (
    <div style={{ minHeight:'60vh', display:'flex', alignItems:'center', justifyContent:'center', background:C.bg }}>
      <div style={{ textAlign:'center', padding:'40px 32px', background:C.card, border:`1px solid ${C.gold}33`, borderRadius:16, maxWidth:380 }}>
        <Crown size={48} style={{ color:C.gold, margin:'0 auto 16px' }}/>
        <h2 style={{ color:C.gold, fontFamily:'Cinzel,serif', marginBottom:8, fontSize:18 }}>Village Admin Console</h2>
        <p style={{ color:C.muted, fontSize:13, lineHeight:1.6 }}>Admin login required. Only Platform Admins, Festival Coordinators, and Community Managers can access this console.</p>
      </div>
    </div>
  );

  const TABS = [
    { id:'dashboard'     as Tab, label:'📊 Dashboard',      },
    { id:'portals'       as Tab, label:'🏘️ All Portals',    },
    { id:'contributions' as Tab, label:'💰 Contributions',  },
    { id:'export'        as Tab, label:'📥 Export & Tools', },
  ];

  return (
    <div style={{ background:C.bg, minHeight:'80vh', paddingBottom:40 }}>
      {/* Header */}
      <div style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, padding:'20px 24px 0' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, flexWrap:'wrap', gap:8 }}>
          <div>
            <h2 style={{ fontSize:20, fontWeight:700, color:C.gold, fontFamily:'Cinzel,serif', margin:0 }}>
              🏘️ Village Admin Console
            </h2>
            <p style={{ fontSize:12, color:C.muted, margin:'4px 0 0' }}>
              Uzairue Kingdom · 22 Villages & 4 Communities · DROP 38 · ERUE Festival 2026
            </p>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            {isAdmin && (
              <button onClick={handleInitAll} disabled={initing}
                style={{ display:'flex', alignItems:'center', gap:5, padding:'8px 14px', background:C.green, border:'none', borderRadius:6, color:C.goldLt, fontSize:12, fontWeight:700, cursor:initing?'not-allowed':'pointer', opacity:initing?0.7:1 }}>
                <Zap size={13}/> {initing ? 'Initializing...' : 'Init All Portals'}
              </button>
            )}
            <button onClick={() => { fetchStatuses(); showMsg('✅ Refreshed'); }}
              style={{ display:'flex', alignItems:'center', gap:5, padding:'7px 12px', background:'none', border:`1px solid ${C.border}`, borderRadius:6, color:C.muted, fontSize:11, cursor:'pointer' }}>
              <RefreshCw size={12}/> Refresh
            </button>
          </div>
        </div>
        <div style={{ display:'flex', gap:2, overflowX:'auto' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ padding:'8px 16px', background:'none', border:'none', borderBottom:tab===t.id?`2px solid ${C.gold}`:'2px solid transparent', color:tab===t.id?C.gold:C.muted, fontSize:12, fontWeight:tab===t.id?700:400, cursor:'pointer', whiteSpace:'nowrap' }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Msg bar */}
      {msg && (
        <div style={{ margin:'0 24px', padding:'10px 16px', background:msg.startsWith('✅')?'#4ADE8022':C.red+'22', border:`1px solid ${msg.startsWith('✅')?'#4ADE8044':C.red+'44'}`, borderRadius:6, color:C.text, fontSize:12, marginTop:12 }}>
          {msg}
        </div>
      )}

      <div style={{ padding:'24px 24px 0' }}>
        {loading
          ? <div style={{ textAlign:'center', padding:'48px 0', color:C.muted }}>
              <RefreshCw size={24} style={{ animation:'spin 1s linear infinite', margin:'0 auto 12px' }}/>
              <div style={{ fontSize:13 }}>Loading village portal data...</div>
            </div>
          : <>
              {tab === 'dashboard'     && <DashboardTab statuses={statuses} initialized={initialized} published={published} complete={complete} avgCompletion={avgCompletion} onSelectVillage={onSelectVillage}/>}
              {tab === 'portals'       && <PortalsTab statuses={filtered} all={statuses} search={search} setSearch={setSearch} filterType={filterType} setFilterType={setFilterType} filterStatus={filterStatus} setFilterStatus={setFilterStatus} onSelectVillage={onSelectVillage} isAdmin={isAdmin} showMsg={showMsg} fetchStatuses={fetchStatuses}/>}
              {tab === 'contributions' && <ContributionsTab contributions={contributions} loading={contribLoading} refresh={fetchContributions}/>}
              {tab === 'export'        && <ExportTab statuses={statuses} handleExport={handleExport} handleInitAll={handleInitAll} initing={initing} isAdmin={isAdmin}/>}
            </>
        }
      </div>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ── Dashboard Tab ─────────────────────────────────────────────────────────────
function DashboardTab({ statuses, initialized, published, complete, avgCompletion, onSelectVillage }: {
  statuses: PortalStatus[]; initialized: number; published: number;
  complete: number; avgCompletion: number;
  onSelectVillage?: (v: { id: string; name: string }) => void;
}) {
  const total = statuses.length;
  const uninit = total - initialized;
  const needsWork = statuses.filter(s => s.isInitialized && !s.published && s.completionPct < 80);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
      {/* KPI Row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:12 }}>
        {[
          { label:'Total Communities', value:`${total}`,       sub:'22 villages + 4 special', color:C.gold },
          { label:'Initialized',       value:`${initialized}`, sub:`${uninit} not yet set up`, color:'#4ADE80' },
          { label:'Published / Live',  value:`${published}`,   sub:`${total-published} private`, color:C.blue },
          { label:'80%+ Complete',     value:`${complete}`,    sub:'portal completion ≥80%', color:'#A78BFA' },
          { label:'Avg Completion',    value:`${avgCompletion}%`, sub:'across all portals', color:avgCompletion>=50?'#4ADE80':C.orange },
        ].map(({ label, value, sub, color }) => (
          <div key={label} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:'16px 18px' }}>
            <div style={{ fontSize:11, color:C.muted, textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>{label}</div>
            <div style={{ fontSize:24, fontWeight:700, color, fontFamily:'Cinzel,serif' }}>{value}</div>
            <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Completion heatmap */}
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:20 }}>
        <div style={{ fontSize:13, color:C.gold, fontWeight:700, marginBottom:14, fontFamily:'Cinzel,serif', letterSpacing:1 }}>
          PORTAL COMPLETION HEATMAP
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))', gap:8 }}>
          {statuses.map(s => {
            const c = s.completionPct >= 80 ? '#4ADE80' : s.completionPct >= 40 ? C.orange : s.isInitialized ? C.red : C.border;
            const comm = [
              { id:'v01', name:'Jattu-Uzairue', icon:'👑' }, { id:'v02', name:'Afashio-Uzairue', icon:'🛡️' },
              { id:'v03', name:'Afowa-Uzairue', icon:'🌳' }, { id:'v04', name:'Apana-Uzairue', icon:'🌊' },
              { id:'v05', name:'Ayaoghena-Uzairue', icon:'🎭' }, { id:'v06', name:'Ayogwiri-Uzairue', icon:'⚔️' },
              { id:'v07', name:'Ayua-Uzairue', icon:'🌿' }, { id:'v08', name:'Elele-Uzairue', icon:'🌾' },
              { id:'v09', name:'Idatto-Uzairue', icon:'🏺' }, { id:'v10', name:'Ikabigbo-Uzairue', icon:'🥊' },
              { id:'v11', name:'Ikholo-Uzairue', icon:'⚖️' }, { id:'v12', name:'Imeke-Uzairue', icon:'🧵' },
              { id:'v13', name:'Imhonikhe-Uzairue', icon:'🏛️' }, { id:'v14', name:'Irekpai-Uzairue', icon:'⚒️' },
              { id:'v15', name:'Iyamho-Uzairue', icon:'📚' }, { id:'v16', name:'Iyora-Uzairue', icon:'🎪' },
              { id:'v17', name:'Iyuku-Uzairue', icon:'🏪' }, { id:'v18', name:'Ogbido-Uzairue', icon:'🌿' },
              { id:'v19', name:'Ozor-Uzairue', icon:'🌲' }, { id:'v20', name:'Ugbenor-Uzairue', icon:'🌴' },
              { id:'v21', name:'Uluoke-Uzairue', icon:'⛰️' }, { id:'v22', name:'Yelwa-Uzairue', icon:'💰' },
              { id:'sp01', name:'Igala Community', icon:'🤝' }, { id:'sp02', name:'Igbo Community', icon:'🌟' },
              { id:'sp03', name:'Yoruba Community', icon:'🎺' }, { id:'sp04', name:'Diaspora Community', icon:'✈️' },
            ].find(x => x.id === s.villageId);
            return (
              <div key={s.villageId}
                onClick={() => onSelectVillage?.({ id: s.villageId, name: s.villageName })}
                style={{ background:C.surface, border:`1px solid ${c}44`, borderRadius:8, padding:'10px 12px', cursor:'pointer', borderLeft:`3px solid ${c}` }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
                  <span style={{ fontSize:16 }}>{comm?.icon||'🏘️'}</span>
                  <span style={{ fontSize:10, color:c, fontWeight:700 }}>{s.completionPct}%</span>
                </div>
                <div style={{ fontSize:10, color:C.text, fontWeight:600, marginBottom:2, lineHeight:1.3 }}>
                  {s.villageName.replace('-Uzairue','').replace('Community','').trim()}
                </div>
                <div style={{ height:3, background:C.border, borderRadius:2 }}>
                  <div style={{ height:'100%', width:`${s.completionPct}%`, background:c, borderRadius:2 }}/>
                </div>
                <div style={{ display:'flex', gap:4, marginTop:5 }}>
                  {s.published && <span style={{ fontSize:8, background:'#4ADE8022', color:'#4ADE80', borderRadius:3, padding:'1px 4px' }}>LIVE</span>}
                  {!s.isInitialized && <span style={{ fontSize:8, background:C.border, color:C.muted, borderRadius:3, padding:'1px 4px' }}>EMPTY</span>}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ display:'flex', gap:16, marginTop:14, fontSize:11, color:C.muted }}>
          {[{ c:'#4ADE80', l:'≥80% complete' },{ c:C.orange, l:'40–79%' },{ c:C.red, l:'<40%' },{ c:C.border, l:'Not initialized' }].map(({ c, l }) => (
            <div key={l} style={{ display:'flex', alignItems:'center', gap:5 }}>
              <div style={{ width:10, height:10, background:c, borderRadius:2 }}/>{l}
            </div>
          ))}
        </div>
      </div>

      {/* Needs Attention */}
      {needsWork.length > 0 && (
        <div style={{ background:C.card, border:`1px solid ${C.orange}44`, borderRadius:12, padding:20 }}>
          <div style={{ fontSize:13, color:C.orange, fontWeight:700, marginBottom:14, fontFamily:'Cinzel,serif', letterSpacing:1 }}>
            ⚠️ NEEDS ATTENTION ({needsWork.length} portals)
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {needsWork.slice(0,8).map(s => (
              <div key={s.villageId}
                onClick={() => onSelectVillage?.({ id: s.villageId, name: s.villageName })}
                style={{ display:'flex', justifyContent:'space-between', alignItems:'center', background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, padding:'10px 14px', cursor:'pointer' }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{s.villageName}</div>
                  <div style={{ fontSize:11, color:C.muted }}>
                    {!s.hasChief && '❌ No chief · '}
                    {!s.hasHistory && '❌ No history · '}
                    {!s.hasGallery && '❌ No gallery · '}
                    {!s.hasContact && '❌ No contact'}
                  </div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:14, fontWeight:700, color:s.completionPct>=40?C.orange:C.red }}>{s.completionPct}%</div>
                  <div style={{ fontSize:10, color:C.muted }}>complete</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Field completion breakdown */}
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:20 }}>
        <div style={{ fontSize:13, color:C.gold, fontWeight:700, marginBottom:14, fontFamily:'Cinzel,serif', letterSpacing:1 }}>
          FIELD COMPLETION ACROSS ALL PORTALS
        </div>
        {[
          { label:'Hero Image',         count: statuses.filter(s=>s.hasHero).length },
          { label:'Chief Information',  count: statuses.filter(s=>s.hasChief).length },
          { label:'Community History',  count: statuses.filter(s=>s.hasHistory).length },
          { label:'Photo Gallery',      count: statuses.filter(s=>s.hasGallery).length },
          { label:'Contact Details',    count: statuses.filter(s=>s.hasContact).length },
          { label:'Team Members',       count: statuses.filter(s=>s.teamCount>0).length },
          { label:'News Posts',         count: statuses.filter(s=>s.postCount>0).length },
          { label:'Natural Resources',  count: statuses.filter(s=>s.resourceCount>0).length },
        ].map(({ label, count }) => {
          const pctVal = statuses.length > 0 ? Math.round((count / statuses.length) * 100) : 0;
          const col = pctVal >= 75 ? '#4ADE80' : pctVal >= 40 ? C.orange : C.red;
          return (
            <div key={label} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
              <div style={{ width:160, fontSize:12, color:C.text, flexShrink:0 }}>{label}</div>
              <div style={{ flex:1, height:8, background:C.border, borderRadius:4 }}>
                <div style={{ height:'100%', width:`${pctVal}%`, background:col, borderRadius:4, transition:'width 0.4s' }}/>
              </div>
              <div style={{ width:60, fontSize:12, color:col, textAlign:'right', fontWeight:700 }}>
                {count}/{statuses.length}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Portals Tab ───────────────────────────────────────────────────────────────
function PortalsTab({ statuses, all, search, setSearch, filterType, setFilterType, filterStatus, setFilterStatus, onSelectVillage, isAdmin, showMsg, fetchStatuses }: {
  statuses: PortalStatus[]; all: PortalStatus[];
  search: string; setSearch: (v: string) => void;
  filterType: string; setFilterType: (v: any) => void;
  filterStatus: string; setFilterStatus: (v: any) => void;
  onSelectVillage?: (v: { id: string; name: string }) => void;
  isAdmin: boolean; showMsg: (m: string) => void; fetchStatuses: () => void;
}) {
  const togglePublish = async (s: PortalStatus) => {
    try {
      const r = await fetch(`/api/community-portals/${s.villageId}`, {
        method:'PUT',
        headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('erue_auth_token')||sessionStorage.getItem('erue_auth_token')||''}` },
        body: JSON.stringify({ published: !s.published }),
      });
      const d = await r.json();
      if (d.success) { showMsg(`✅ ${s.villageName} ${!s.published?'published':'unpublished'}`); fetchStatuses(); }
      else showMsg('❌ ' + d.error);
    } catch { showMsg('❌ Network error'); }
  };

  const COMM_MAP: Record<string, { icon: string; type: string }> = {};
  ALL_COMMUNITIES.forEach(c => { COMM_MAP[c.id] = { icon: c.icon, type: c.type }; });

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      {/* Filters */}
      <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ position:'relative', flex:1, minWidth:200 }}>
          <Search size={13} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:C.muted }}/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search villages..."
            style={{ width:'100%', padding:'7px 10px 7px 30px', background:C.card, border:`1px solid ${C.border}`, borderRadius:6, color:C.text, fontSize:12, boxSizing:'border-box' }}/>
        </div>
        <select value={filterType} onChange={e=>setFilterType(e.target.value)}
          style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:6, color:C.text, padding:'6px 10px', fontSize:12 }}>
          <option value="all">All Types</option>
          <option value="village">Villages (22)</option>
          <option value="special">Special Communities (4)</option>
        </select>
        <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
          style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:6, color:C.text, padding:'6px 10px', fontSize:12 }}>
          <option value="all">All Status</option>
          <option value="published">Published / Live</option>
          <option value="draft">Draft (Initialized)</option>
          <option value="empty">Not Initialized</option>
        </select>
        <span style={{ fontSize:11, color:C.muted }}>{statuses.length} of {all.length}</span>
      </div>

      {/* Grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:12 }}>
        {statuses.map(s => {
          const comm = COMM_MAP[s.villageId] || { icon:'🏘️', type:'village' };
          const col  = s.completionPct >= 80 ? '#4ADE80' : s.completionPct >= 40 ? C.orange : s.isInitialized ? C.red : C.border;

          return (
            <div key={s.villageId} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden' }}>
              {/* Card header */}
              <div style={{ background:`linear-gradient(135deg,${C.green} 0%,#0a2518 100%)`, padding:'12px 16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontSize:24 }}>{comm.icon}</span>
                  <div>
                    <div style={{ fontFamily:'Cinzel,serif', color:C.goldLt, fontWeight:700, fontSize:13 }}>
                      {s.villageName}
                    </div>
                    <div style={{ fontSize:10, color:C.muted, textTransform:'uppercase' }}>
                      {comm.type === 'special' ? 'Special Community' : 'Village'} · {s.villageId}
                    </div>
                  </div>
                </div>
                <div style={{ textAlign:'right' }}>
                  {s.published
                    ? <span style={{ fontSize:10, background:'#4ADE8022', color:'#4ADE80', border:'1px solid #4ADE8044', borderRadius:10, padding:'2px 8px' }}>🌐 LIVE</span>
                    : s.isInitialized
                    ? <span style={{ fontSize:10, background:C.orange+'22', color:C.orange, border:`1px solid ${C.orange}44`, borderRadius:10, padding:'2px 8px' }}>⏸ DRAFT</span>
                    : <span style={{ fontSize:10, background:C.border, color:C.muted, border:`1px solid ${C.border}`, borderRadius:10, padding:'2px 8px' }}>○ EMPTY</span>
                  }
                </div>
              </div>

              {/* Progress bar */}
              <div style={{ height:4, background:C.border }}>
                <div style={{ height:'100%', width:`${s.completionPct}%`, background:col, transition:'width 0.4s' }}/>
              </div>

              {/* Field checklist */}
              <div style={{ padding:'12px 16px' }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:6, marginBottom:12 }}>
                  {[
                    { label:'Hero', done:s.hasHero }, { label:'Chief', done:s.hasChief },
                    { label:'History', done:s.hasHistory }, { label:'Gallery', done:s.hasGallery },
                    { label:'Contact', done:s.hasContact }, { label:'Team', done:s.teamCount>0 },
                  ].map(({ label, done }) => (
                    <div key={label} style={{ display:'flex', alignItems:'center', gap:4, fontSize:10 }}>
                      <span style={{ color:done?'#4ADE80':C.border, fontSize:12 }}>{done?'✓':'○'}</span>
                      <span style={{ color:done?C.text:C.muted }}>{label}</span>
                    </div>
                  ))}
                </div>

                {/* Stats row */}
                <div style={{ display:'flex', gap:10, fontSize:11, color:C.muted, marginBottom:12 }}>
                  <span>👥 {s.teamCount} team</span>
                  <span>📰 {s.postCount} posts</span>
                  <span>🌱 {s.resourceCount} resources</span>
                  <span style={{ marginLeft:'auto', color:col, fontWeight:700 }}>{s.completionPct}%</span>
                </div>

                {/* Actions */}
                <div style={{ display:'flex', gap:8 }}>
                  <button
                    onClick={() => onSelectVillage?.({ id: s.villageId, name: s.villageName })}
                    style={{ flex:1, padding:'7px 0', background:C.green, border:'none', borderRadius:6, color:C.goldLt, fontSize:11, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
                    <Edit3 size={11}/> Edit Portal
                  </button>
                  {isAdmin && s.isInitialized && (
                    <button
                      onClick={() => togglePublish(s)}
                      style={{ padding:'7px 12px', background:s.published?C.surface:C.gold+'22', border:`1px solid ${s.published?C.border:C.gold+'44'}`, borderRadius:6, color:s.published?C.muted:C.gold, fontSize:11, cursor:'pointer' }}>
                      {s.published ? '⏸' : '🌐'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {statuses.length === 0 && (
        <div style={{ textAlign:'center', padding:'48px 24px', background:C.card, borderRadius:12, border:`1px solid ${C.border}`, color:C.muted, fontSize:13 }}>
          No communities match your filters
        </div>
      )}
    </div>
  );
}

// ── Contributions Tab ─────────────────────────────────────────────────────────
function ContributionsTab({ contributions, loading, refresh }: {
  contributions: any[]; loading: boolean; refresh: () => void;
}) {
  const totalAmount = contributions.reduce((s: number, c: any) => s + (Number(c.amount) || 0), 0);
  const fmt = (n: number) => '₦' + n.toLocaleString('en-NG', { minimumFractionDigits:0 });

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
        {[
          { label:'Total Contributions', value: String(contributions.length), color: C.gold },
          { label:'Total Amount',        value: fmt(totalAmount),             color: '#4ADE80' },
          { label:'Villages Contributing', value: String(new Set(contributions.map((c:any)=>c.villageId)).size), color: C.blue },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:'16px 20px', flex:1, minWidth:160 }}>
            <div style={{ fontSize:11, color:C.muted, textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>{label}</div>
            <div style={{ fontSize:22, fontWeight:700, color, fontFamily:'Cinzel,serif' }}>{value}</div>
          </div>
        ))}
      </div>

      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden' }}>
        <div style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:13, color:C.gold, fontWeight:700, fontFamily:'Cinzel,serif' }}>Village Contributions</div>
          <button onClick={refresh} style={{ background:'none', border:`1px solid ${C.border}`, borderRadius:6, color:C.muted, padding:'5px 10px', cursor:'pointer', fontSize:11, display:'flex', alignItems:'center', gap:4 }}>
            <RefreshCw size={11}/> Refresh
          </button>
        </div>
        {loading ? (
          <div style={{ padding:'32px 16px', textAlign:'center', color:C.muted }}>Loading contributions...</div>
        ) : contributions.length === 0 ? (
          <div style={{ padding:'32px 16px', textAlign:'center', color:C.muted, fontSize:13 }}>
            No village contributions recorded yet. Contributions from the Accounting module will appear here when tagged with a village ID.
          </div>
        ) : (
          contributions.slice(0,50).map((c: any, i: number) => (
            <div key={i} style={{ display:'flex', gap:12, padding:'10px 16px', borderBottom:`1px solid ${C.border}`, alignItems:'center' }}>
              <div style={{ flex:2 }}>
                <div style={{ fontSize:12, color:C.text, fontWeight:600 }}>{c.villageName || c.villageId || 'Unknown Village'}</div>
                <div style={{ fontSize:10, color:C.muted }}>{c.description || c.category || 'Contribution'} · {c.date || c.createdAt || ''}</div>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontSize:13, fontWeight:700, color:'#4ADE80' }}>{fmt(Number(c.amount)||0)}</div>
                <div style={{ fontSize:10, color:C.muted }}>{c.type || 'contribution'}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── Export & Tools Tab ────────────────────────────────────────────────────────
function ExportTab({ statuses, handleExport, handleInitAll, initing, isAdmin }: {
  statuses: PortalStatus[]; handleExport: () => void;
  handleInitAll: () => void; initing: boolean; isAdmin: boolean;
}) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:16 }}>

        {/* Export CSV */}
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:24 }}>
          <div style={{ fontSize:32, marginBottom:12 }}>📥</div>
          <h3 style={{ fontFamily:'Cinzel,serif', color:C.goldLt, fontWeight:700, fontSize:16, marginBottom:8 }}>Export Village Data</h3>
          <p style={{ color:C.muted, fontSize:13, marginBottom:16, lineHeight:1.5 }}>
            Download a CSV file with status, completion, and metadata for all 26 communities.
          </p>
          <button onClick={handleExport}
            style={{ width:'100%', padding:'10px 0', background:C.gold, border:'none', borderRadius:8, color:'#000', fontWeight:700, fontSize:13, cursor:'pointer' }}>
            📥 Download CSV Report
          </button>
        </div>

        {/* Bulk Initialize */}
        {isAdmin && (
          <div style={{ background:C.card, border:`1px solid ${C.orange}44`, borderRadius:12, padding:24 }}>
            <div style={{ fontSize:32, marginBottom:12 }}>⚡</div>
            <h3 style={{ fontFamily:'Cinzel,serif', color:C.orange, fontWeight:700, fontSize:16, marginBottom:8 }}>Bulk Initialize Portals</h3>
            <p style={{ color:C.muted, fontSize:13, marginBottom:16, lineHeight:1.5 }}>
              Create empty portal records for all 26 communities that haven't been initialized yet. Existing portals are never overwritten.
            </p>
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12, color:C.muted }}>
              📊 Status: <strong style={{ color:C.text }}>{statuses.filter(s=>s.isInitialized).length}</strong> of {statuses.length} initialized
            </div>
            <button onClick={handleInitAll} disabled={initing}
              style={{ width:'100%', padding:'10px 0', background:C.orange, border:'none', borderRadius:8, color:'#000', fontWeight:700, fontSize:13, cursor:initing?'not-allowed':'pointer', opacity:initing?0.7:1 }}>
              {initing ? '⏳ Initializing...' : '⚡ Initialize All Missing Portals'}
            </button>
          </div>
        )}

        {/* Portal checklist summary */}
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:24 }}>
          <div style={{ fontSize:32, marginBottom:12 }}>📋</div>
          <h3 style={{ fontFamily:'Cinzel,serif', color:C.goldLt, fontWeight:700, fontSize:16, marginBottom:8 }}>Pre-Launch Checklist</h3>
          <p style={{ color:C.muted, fontSize:13, marginBottom:16, lineHeight:1.5 }}>
            Festival opens December 24, 2026. All village portals should be complete before launch.
          </p>
          {[
            { label:'All 26 portals initialized', done: statuses.filter(s=>s.isInitialized).length === 26 },
            { label:'All portals have chief info',  done: statuses.filter(s=>s.hasChief).length === 26 },
            { label:'All portals have history',     done: statuses.filter(s=>s.hasHistory).length === 26 },
            { label:'All portals have gallery',     done: statuses.filter(s=>s.hasGallery).length === 26 },
            { label:'All portals published/live',   done: statuses.filter(s=>s.published).length === 26 },
          ].map(({ label, done }) => (
            <div key={label} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
              <div style={{ width:16, height:16, borderRadius:'50%', background:done?'#4ADE80':C.border, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                {done && <span style={{ fontSize:9, color:'#000' }}>✓</span>}
              </div>
              <span style={{ fontSize:12, color:done?'#4ADE80':C.muted }}>{label}</span>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}
